import React from 'react';

import { CommonAdminFunctionsProps } from "./admin-functions.types";
import { ProfileSelectorItem } from '@common/components/profile-selector';
import { UserMaintenanceProfile } from '@common/dto/user-maintenance/profile';
import { ColumnListDto } from '@common/dto/column/list-dto';
import { HttpErrorDto } from '@common/dto/http';
import { I18n } from 'react-redux-i18n';

type ControllerProps = {
  columns: Array<ColumnListDto>;
  setChangingColumns: (isChanging: boolean) => void;
};

export const useAdminFunctionsController = (props: CommonAdminFunctionsProps, params: ControllerProps) => {
  const { getProfiles: getProfilesBase, showErrorMessage, onApplyCurrentColumnsToSpecificUsers, onSetDefaultColumnsForSpecificUsers, onApplyCurrentColumnsToAllUsers, onSetDefaultColumnsForAllUsers, onRestoreColumnsToDefault } = props;
  const { columns, setChangingColumns } = params;
  const [profileSelectorOpened, setProfileSelectorOpened] = React.useState(false);
  type ProfileSelectorTrigger = 'defaultColumns' | 'currentColumns';
  const [profileSelectorTrigger, setProfileSelectorTrigger] = React.useState<ProfileSelectorTrigger | undefined>();

  const closeProfileSelector = React.useCallback(() => {
    setProfileSelectorOpened(false);
  }, []);

  const openProfileSelector = React.useCallback(() => {
    setProfileSelectorOpened(true);
  }, []);

  const handleProfileSelectorSubmit = async (users: Array<ProfileSelectorItem>) => {
    if (!profileSelectorTrigger) {
      return;
    }

    try {
      setChangingColumns(true);

      const handlerConfig: Record<ProfileSelectorTrigger, (users: Array<UserMaintenanceProfile['name']>) => Promise<void>> = {
        currentColumns: (users: Array<UserMaintenanceProfile['name']>) => onApplyCurrentColumnsToSpecificUsers(columns, users),
        defaultColumns: (users: Array<UserMaintenanceProfile['name']>) => onSetDefaultColumnsForSpecificUsers(columns, users),
      };
      const handle = handlerConfig[profileSelectorTrigger];
      await handle(users.map((x) => x.id));
      closeProfileSelector();
    } catch (err) {
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to update current columns'));
    } finally {
      setChangingColumns(false);
    }
  };

  const getProfiles = React.useCallback(() => {
    return getProfilesBase().then((res) => {
      return res.map((x): ProfileSelectorItem => {
        return {
          id: x.name,
          ...x,
        };
      });
    });
  }, []);

  const handleSetDefaultColumnsForSpecificUsers = React.useCallback(() => {
    openProfileSelector();
    setProfileSelectorTrigger('defaultColumns');
  }, []);

  const handleApplyCurrentColumnsToSpecificUsers = React.useCallback(() => {
    openProfileSelector();
    setProfileSelectorTrigger('currentColumns');
  }, []);

  const handleApplyCurrentColumnsToAllUsers = React.useCallback(async () => {
    try {
      setChangingColumns(true);
      await onApplyCurrentColumnsToAllUsers(columns);
    } catch (err) {
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to apply current columns to all users'));
    } finally {
      setChangingColumns(false);
    }
  }, [columns, onApplyCurrentColumnsToAllUsers, showErrorMessage, setChangingColumns]);

  const handleSetDefaultColumnsForAllUsers = React.useCallback(async () => {
    try {
      setChangingColumns(true);
      await onSetDefaultColumnsForAllUsers(columns);
    } catch (err) {
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to apply current columns to all users'));
    } finally {
      setChangingColumns(false);
    }
  }, [columns, onSetDefaultColumnsForAllUsers, showErrorMessage, setChangingColumns]);

  const handleRestoreColumnsToDefault = React.useCallback(async () => {
    try {
      setChangingColumns(true);
      await onRestoreColumnsToDefault();
    } catch (err) {
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to restore column to default'));
    } finally {
      setChangingColumns(false);
    }
  }, [onRestoreColumnsToDefault, showErrorMessage, setChangingColumns]);

  return {
    handleRestoreColumnsToDefault,
    onSetDefaultColumnsForAllUsers,
    handleSetDefaultColumnsForAllUsers,
    handleApplyCurrentColumnsToAllUsers,
    handleSetDefaultColumnsForSpecificUsers,
    handleApplyCurrentColumnsToSpecificUsers,
    getProfiles,
    profileSelectorOpened,
    closeProfileSelector,
    handleProfileSelectorSubmit,
  };
};